import React, { useEffect } from 'react'
import LoginHelpTrigger from '@/components/auth/login/LoginHelpTrigger'
import type { Identifier, IdentifierType, DeadEndReason } from '@/hooks/useLoginFlow'
import { ORDER_EDITING_DISABLED_PHONE } from '@/config/maintenance'
import logger from '@/utils/logger'

/**
 * Step 2 — an identifier we can't sign in. Maps to the backend's three dead
 * ends: `not_found`, `ambiguous_identifier` and `no_contact_method`.
 *
 * All three land here rather than each getting a screen. A customer whose
 * mobile is on two records and one whose record has only a landline are both
 * stuck for reasons they cannot act on, and both need the same two things:
 * the other ways of signing in, and a way to reach us. Naming the reason
 * would also confirm to anyone typing values that the identifier belongs to a
 * real customer, which is what the wording below is written to avoid. The
 * reason is logged instead, and customer services can see it.
 *
 * The copy deliberately does not say the account was not found. It used to
 * say the account "hasn't been moved to the new portal yet", which is no
 * longer true now that every customer has been migrated, and which confirmed
 * to anyone asking that a given address is not a customer. Someone probing
 * addresses would rather be told nothing.
 *
 * This is a smaller change than it looks: it removes the tell a person would
 * read, not the one a script would. Each outcome of checkIdentity still
 * renders its own screen, so an address that exists is still distinguishable
 * from one that does not by anything watching responses rather than words.
 * Closing that properly means this route producing the same outcome as a
 * successful one, which is a flow decision rather than a copy change.
 *
 * Two cases this handles:
 *   1. Only ONE identifier type tried so far. Suggest the others.
 *   2. Both tried this session. Point at customer services rather than
 *      repeating a suggestion that has already failed.
 *
 * The "tried both" tracker lives in useLoginFlow (state.triedAndUnknown
 * and the derived hasTriedBothIdentifierTypes flag). Refresh wipes
 * the tracker; that's intentional, a deliberate retry should get a
 * fresh experience.
 *
 * No PII rendered. The body suggests the OTHER identifier type by
 * name only, never echoes anything the customer typed.
 */

const LABELS: Record<IdentifierType, string> = {
  email: 'email address',
  mobile: 'mobile number',
  customer_number: 'customer number',
}

/** "mobile number or customer number", "email address, mobile number or customer number". */
const listTypes = (types: IdentifierType[]): string => {
  const names = types.map(t => LABELS[t])

  return names.length < 2
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}

interface Props {
  identifier: Identifier
  triedIdentifierType: IdentifierType
  /** The ways in they have not yet had turned away this session. */
  untriedIdentifierTypes: IdentifierType[]
  hasTriedAllIdentifierTypes: boolean
  /** Logged, never rendered. See the note above. */
  reason: DeadEndReason
  onBack: () => void
}

function LoginNotFoundStep({
  identifier,
  triedIdentifierType,
  untriedIdentifierTypes,
  hasTriedAllIdentifierTypes,
  reason,
  onBack,
}: Props): React.JSX.Element {
  const triedLabel = LABELS[triedIdentifierType]

  // Which dead end customers actually hit is worth knowing, and belongs in
  // our logs rather than on their screen.
  useEffect(() => {
    logger.info('Login dead end reached', { reason, triedIdentifierType })
  }, [reason, triedIdentifierType])

  return (
    <div className="login-step login-step--neutral">
      <h1 className="login-step-heading">
        Oops! We&rsquo;re having trouble identifying your account
      </h1>

      {hasTriedAllIdentifierTypes ? (
        <p className="login-step-subheading">
          We still can&rsquo;t identify your account from what you&rsquo;ve
          tried.
        </p>
      ) : (
        <p className="login-step-subheading">
          Please check you&rsquo;ve entered your <strong>{triedLabel}</strong>{' '}
          correctly, or try signing in using your{' '}
          <strong>{listTypes(untriedIdentifierTypes)}</strong> instead.
        </p>
      )}

      <button type="button" className="btn btn-primary btn-lg btn-block" onClick={onBack}>
        Back to sign in
      </button>

      <div className="login-step-footer login-step-footer--stacked">
        <p className="login-step-footer-heading">Still having trouble?</p>
        <p className="login-step-footer-line">
          <LoginHelpTrigger identifier={identifier} label="Raise a query" /> and
          we&rsquo;ll get back to you as quickly as possible.
        </p>
        <p className="login-step-footer-line">
          Call our friendly team on{' '}
          <a href={`tel:${ORDER_EDITING_DISABLED_PHONE.replace(/\s/g, '')}`}>
            {ORDER_EDITING_DISABLED_PHONE}
          </a>{' '}
          and we&rsquo;ll help get you set up.
        </p>
      </div>
    </div>
  )
}

export default LoginNotFoundStep
